import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";

interface HACCPDoc { id: string; type: string; title: string; location_name: string; generated_at: string; data: any; }
interface Location { id: string; name: string; client_name: string; }

export function HACCP() {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState<HACCPDoc[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<HACCPDoc | null>(null);
  const [generating, setGenerating] = useState(false);

  const loadDocuments = async () => { setDocuments(await api("/haccp/documents")); };
  const loadLocations = async () => { setLocations(await api("/locations")); };

  useEffect(() => { loadDocuments(); loadLocations(); }, []);

  const generate = async (locationId: string, type: "monitoring-plan" | "inspection-report") => {
    setGenerating(true);
    try {
      await api(`/haccp/generate/${type}`, { method: "POST", body: JSON.stringify({ locationId }) });
      loadDocuments();
    } finally { setGenerating(false); }
  };

  const viewDocument = async (id: string) => { setSelectedDoc(await api(`/haccp/documents/${id}`)); };

  if (selectedDoc) {
    return (
      <div className="animate-fade-in">
        <button onClick={() => setSelectedDoc(null)} className="inline-flex items-center gap-1.5 text-sm text-navy-400 hover:text-navy-600 transition-colors mb-6 no-print">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
          {t("common.back")}
        </button>

        <div className="bg-white rounded-xl border border-navy-100 p-8 print:border-none print:shadow-none print:p-0">
          <div className="flex justify-between items-start mb-8 no-print">
            <div>
              <div className={`badge mb-3 ${selectedDoc.type === "monitoring_plan" ? "badge-scheduled" : "badge-completed"}`}>
                {selectedDoc.type === "monitoring_plan" ? t("haccp.monitoringPlan") : t("haccp.inspectionReport")}
              </div>
              <h1 className="text-xl font-bold" style={{ fontFamily: "var(--font-display)" }}>{selectedDoc.title}</h1>
              <p className="text-sm text-navy-400 mt-1">
                {t("haccp.generatedOn", { date: new Date(selectedDoc.generated_at).toLocaleDateString() })}
              </p>
            </div>
            <button onClick={() => window.print()} className="btn btn-ghost btn-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" />
              </svg>
              {t("haccp.printPdf")}
            </button>
          </div>

          {selectedDoc.type === "monitoring_plan" && <MonitoringPlanView data={selectedDoc.data} />}
          {selectedDoc.type === "inspection_report" && <InspectionReportView data={selectedDoc.data} />}
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          {t("haccp.title")}
        </h1>
        <p className="text-sm text-navy-400 mt-1">{t("haccp.subtitle")}</p>
      </div>

      {/* Generate per location */}
      {locations.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xs font-semibold text-navy-400 uppercase tracking-widest mb-3">{t("haccp.generateDocuments")}</h2>
          <div className="grid gap-3">
            {locations.map((loc) => (
              <div key={loc.id} className="bg-white rounded-xl border border-navy-100 p-4 flex items-center justify-between card-lift">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-navy-50 rounded-lg flex items-center justify-center text-navy-400">
                    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium">{loc.name}</p>
                    <p className="text-xs text-navy-400">{loc.client_name}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => generate(loc.id, "monitoring-plan")} disabled={generating} className="btn btn-secondary btn-sm">
                    {t("haccp.generatePlan")}
                  </button>
                  <button onClick={() => generate(loc.id, "inspection-report")} disabled={generating} className="btn btn-primary btn-sm">
                    {t("haccp.generateReport")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Document list */}
      <h2 className="text-xs font-semibold text-navy-400 uppercase tracking-widest mb-3">{t("haccp.generatedDocuments")}</h2>
      {documents.length === 0 ? (
        <div className="bg-white rounded-xl border border-navy-100 p-16 text-center">
          <div className="w-14 h-14 bg-navy-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-navy-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
          </div>
          <p className="text-navy-600 font-medium mb-1">{t("haccp.noDocumentsTitle")}</p>
          <p className="text-navy-400 text-sm">{t("haccp.noDocuments")}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
          {documents.map((doc, i) => (
            <button key={doc.id} onClick={() => viewDocument(doc.id)} className={`w-full text-left px-5 py-4 flex items-center justify-between hover:bg-navy-50/50 transition-colors ${i < documents.length - 1 ? "border-b border-navy-50" : ""}`}>
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${doc.type === "monitoring_plan" ? "bg-sky-50 text-sky-500" : "bg-emerald-50 text-emerald-500"}`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-navy-900">{doc.title}</p>
                  <p className="text-xs text-navy-400">{doc.location_name} · {new Date(doc.generated_at).toLocaleDateString()}</p>
                </div>
              </div>
              <svg className="w-4 h-4 text-navy-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MonitoringPlanView({ data }: { data: any }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-8">
      <section>
        <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: "var(--font-display)" }}>{t("haccp.locationDetails")}</h2>
        <div className="bg-navy-50 rounded-xl p-5">
          <dl className="grid grid-cols-2 gap-y-3 gap-x-8 text-sm">
            <dt className="text-navy-400">{t("haccp.client")}</dt><dd className="font-medium">{data.client?.name}</dd>
            <dt className="text-navy-400">{t("haccp.location")}</dt><dd className="font-medium">{data.location?.name}</dd>
            <dt className="text-navy-400">{t("haccp.address")}</dt><dd className="font-medium">{data.location?.address}</dd>
            <dt className="text-navy-400">{t("haccp.locationType")}</dt><dd className="font-medium capitalize">{data.location?.type ? t(`locations.types.${data.location.type}`) : data.location?.type}</dd>
          </dl>
        </div>
      </section>
      <section>
        <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: "var(--font-display)" }}>
          {t("haccp.monitoringStationsCount", { count: data.stations?.length ?? 0 })}
        </h2>
        {data.stations?.length > 0 ? (
          <div className="overflow-hidden rounded-xl border border-navy-100">
            <table className="w-full text-sm">
              <thead><tr className="bg-navy-50 text-navy-500 text-xs uppercase tracking-wider">
                <th className="px-4 py-3 text-left font-medium">{t("haccp.code")}</th>
                <th className="px-4 py-3 text-left font-medium">{t("haccp.locationType")}</th>
                <th className="px-4 py-3 text-left font-medium">{t("haccp.position")}</th>
              </tr></thead>
              <tbody>
                {data.stations.map((s: any, i: number) => (
                  <tr key={i} className={i < data.stations.length - 1 ? "border-b border-navy-50" : ""}>
                    <td className="px-4 py-3 font-mono font-medium text-navy-900">{s.code}</td>
                    <td className="px-4 py-3 capitalize text-navy-600">{s.type ? t(`locations.stationTypes.${s.type}`) : s.type?.replace(/_/g, " ")}</td>
                    <td className="px-4 py-3 text-navy-400">{s.position || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-navy-400 text-sm">{t("haccp.noStationsConfigured")}</p>}
      </section>
      <section>
        <h2 className="text-lg font-semibold mb-2" style={{ fontFamily: "var(--font-display)" }}>{t("haccp.serviceProvider")}</h2>
        <p className="text-sm font-medium">{data.company?.name}</p>
      </section>
    </div>
  );
}

function InspectionReportView({ data }: { data: any }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-8">
      <section>
        <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: "var(--font-display)" }}>{t("haccp.reportSummary")}</h2>
        <div className="bg-navy-50 rounded-xl p-5">
          <dl className="grid grid-cols-2 gap-y-3 gap-x-8 text-sm">
            <dt className="text-navy-400">{t("haccp.client")}</dt><dd className="font-medium">{data.client?.name}</dd>
            <dt className="text-navy-400">{t("haccp.location")}</dt><dd className="font-medium">{data.location?.name}</dd>
            <dt className="text-navy-400">{t("haccp.period")}</dt>
            <dd className="font-medium">
              {data.period?.from && new Date(data.period.from).toLocaleDateString()} – {data.period?.to && new Date(data.period.to).toLocaleDateString()}
            </dd>
          </dl>
        </div>
      </section>

      <section>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-navy-50 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold" style={{ fontFamily: "var(--font-display)" }}>{data.summary?.totalVisits ?? 0}</p>
            <p className="text-xs text-navy-400 mt-1">{t("haccp.visits")}</p>
          </div>
          <div className="bg-navy-50 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold" style={{ fontFamily: "var(--font-display)" }}>{data.summary?.totalInspections ?? 0}</p>
            <p className="text-xs text-navy-400 mt-1">{t("haccp.totalInspections")}</p>
          </div>
          {data.summary?.findingsBreakdown && Object.entries(data.summary.findingsBreakdown).map(([finding, count]) => (
            <div key={finding} className={`rounded-xl p-4 text-center ${finding === "none" ? "bg-emerald-50" : "bg-amber-50"}`}>
              <p className={`text-2xl font-bold ${finding === "none" ? "text-emerald-600" : "text-amber-600"}`} style={{ fontFamily: "var(--font-display)" }}>{count as number}</p>
              <p className="text-xs text-navy-400 mt-1">{t(`inspections.findings.${finding}`)}</p>
            </div>
          ))}
        </div>
      </section>

      {data.visits?.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: "var(--font-display)" }}>{t("haccp.visitDetails")}</h2>
          <div className="space-y-4">
            {data.visits.map((visit: any, i: number) => (
              <div key={i} className="border border-navy-100 rounded-xl overflow-hidden">
                <div className="bg-navy-50 px-5 py-3 flex justify-between items-center">
                  <p className="text-sm font-medium">{new Date(visit.date).toLocaleDateString()}</p>
                  <p className="text-xs text-navy-400">{t("haccp.technician", { name: visit.technician || "—" })}</p>
                </div>
                {visit.inspections?.length > 0 ? (
                  <table className="w-full text-xs">
                    <thead><tr className="text-navy-400 border-b border-navy-50">
                      <th className="px-5 py-2 text-left font-medium">{t("haccp.station")}</th>
                      <th className="px-5 py-2 text-left font-medium">{t("haccp.findings")}</th>
                      <th className="px-5 py-2 text-left font-medium">{t("haccp.pest")}</th>
                      <th className="px-5 py-2 text-left font-medium">{t("haccp.actions")}</th>
                    </tr></thead>
                    <tbody>
                      {visit.inspections.map((insp: any, j: number) => (
                        <tr key={j} className={j < visit.inspections.length - 1 ? "border-b border-navy-50" : ""}>
                          <td className="px-5 py-2 font-mono font-medium">{insp.stationCode}</td>
                          <td className="px-5 py-2">{insp.findings ? t(`inspections.findings.${insp.findings}`) : "—"}</td>
                          <td className="px-5 py-2">{insp.pestType ? t(`inspections.pestTypes.${insp.pestType}`) : "—"}</td>
                          <td className="px-5 py-2">{insp.actionsTaken || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <p className="px-5 py-3 text-xs text-navy-400">{t("haccp.noInspectionRecords")}</p>}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
